/**
 * Crime detection on the main thread (M5 spec §12.2, §14.2; plan C14). Reads the combat event ring with its own
 * cursor (like the HUD) and M2's space event ring, and reports crimes through a sink (the subsystem sends
 * `reportCrime`). The worker then applies the law (jurisdiction, bounty, standing, its own throttle).
 *
 * - **Assault / murder:** a hit or kill by the player on a ship whose team is not standing-hostile to the player
 *   (grudges don't count: the first hit makes one), of a non-outlaw faction, that hasn't damaged the player within
 *   law.json `assault.graceS`. Assault is reported at most once per victim per `assault.throttleS`.
 * - **Zone exemption:** a victim whose faction is a side of an active local combat zone, with the player within
 *   zones.json `hudRangeM` of the zone, is zone support (C15), never a crime.
 * - **Witness rule:** a live ship (not the player, not the victim) of the jurisdiction's or the victim's team within
 *   law.json `witness.rangeM` of the player, or a station of either faction within that range of its **hull** (scale
 *   SC-5e2: the bounding sphere, `|p − centre| − R`, so a km-scale station covers the same space off its hull as a
 *   small one). Otherwise nothing is recorded.
 * - **Piracy:** a murder's death point is remembered; a combat wreck spawning there (`SEV.wreckSpawned` within
 *   charter-law.json `wreckMatchM`) is flagged; a part secured (`SEV.partSecured`) within `piracyRangeM` of a
 *   flagged wreck within `piracy.windowS` of the murder is piracy (once per wreck).
 *
 * - **Self-defence (M7 S1, D-339):** an `EngagementLog` remembers, per entity, who opened its current exchange of fire
 *   with the player (an exchange ends after `assault.graceS` with no hit either way). The traffic kill reader keeps one
 *   and sends `selfDefence` with `localDestroyed` when the victim fired first; the worker then logs the guardian kill
 *   without its Holdfast cost (D-338). The detector itself applies no guardian cost.
 *
 * Per-tick work allocates nothing: typed arrays and fixed rings; the sink is bound once.
 */
import { z } from 'zod'
import charterLawJson from '../data/charter-law.json'
import hullsJson from '../data/strategic/hulls.json'
import lawJson from '../data/strategic/law.json'
import waresJson from '../data/strategic/wares.json'
import zonesJson from '../data/strategic/zones.json'
import { EV, eventSlot, firstReadable } from '../combat/events'
import { PLAYER_TEAM_ID, isStandingHostile } from '../combat/hostility'
import { stationRadiusOf } from '../reach/session/siteRadius'
import { firstReadableSpace, SEV, spaceEventSlot, type SpaceEventRing } from '../space/events'
import type { Sim } from '../sim'
import { FACTION, PLAYER_FACTION_ID, type CorpFactionId, type CrimeKind, type FactionId, type StationView } from '../strategic/types'
import { Life, MAX_ENTITIES, Pos64, Ship, Team } from '../world/components'
import { FACTION_OUTLAW, FACTION_TEAM, TEAM_FACTION } from './hostilityWriter'

export const charterLawSchema = z.strictObject({
  comment: z.string(),
  wreckMatchM: z.number().positive(),
  piracyRangeM: z.number().positive(),
})
export type CharterLawConfig = z.infer<typeof charterLawSchema>

/** Everything the detector reads, as live tuning objects (the dev panel may bind them). */
export interface CrimeTuning {
  witnessM: number
  graceS: number
  throttleS: number
  piracyWindowS: number
  zoneRangeM: number
  wreckMatchM: number
  piracyRangeM: number
}

export function defaultCrimeTuning(): CrimeTuning {
  const local = charterLawSchema.parse(charterLawJson)
  return {
    witnessM: lawJson.witness.rangeM,
    graceS: lawJson.crimes.assault.graceS,
    throttleS: lawJson.crimes.assault.throttleS,
    piracyWindowS: lawJson.crimes.piracy.windowS,
    zoneRangeM: zonesJson.hudRangeM,
    wreckMatchM: local.wreckMatchM,
    piracyRangeM: local.piracyRangeM,
  }
}

/** Local stations and zones the law reads (filled at message rate from the manifest and the system view). */
export const LAW_STATION_CAPACITY = 64
export const LAW_ZONE_CAPACITY = 16

export interface LawWorld {
  /** The local system id (null outside a system). */
  systemId: string | null
  /** The jurisdiction: the holder's faction index when it isn't an outlaw, else −1 (lawless). */
  jurisdiction: number
  /** The claim holder's faction index (−1 = unclaimed or the corporation), outlaw or not. */
  holder: number
  /**
   * The player corporation holds the system (M5 spec §27.2, D-424). `holder` and `jurisdiction` stay −1 there: the
   * corporation is never a law faction, so it books, scans and refuses nobody.
   */
  corpHeld: boolean
  stations: number
  readonly stationIds: string[]
  /** Each station's owner as a law faction index (−1 = no owner or the corporation; `stationCorp` tells them apart). */
  readonly stationOwner: Int8Array
  /** 1 when the player corporation owns the station (§27.3: no docking scan, no refusal there). */
  readonly stationCorp: Uint8Array
  readonly stationDisabled: Uint8Array
  readonly sx: Float64Array
  readonly sy: Float64Array
  readonly sz: Float64Array
  /**
   * Each station's gameplay radius R (metres; scale SC-5e2): station law ranges (witness, fines) count from the hull,
   * `|p − (sx, sy, sz)| − R`. 0 = from the centre (a model the manifest lacks).
   */
  readonly sr: Float64Array
  zones: number
  readonly zoneA: Int8Array
  readonly zoneB: Int8Array
  readonly zx: Float64Array
  readonly zy: Float64Array
  readonly zz: Float64Array
}

export function createLawWorld(): LawWorld {
  return {
    systemId: null,
    jurisdiction: -1,
    holder: -1,
    corpHeld: false,
    stations: 0,
    stationIds: [],
    stationOwner: new Int8Array(LAW_STATION_CAPACITY),
    stationCorp: new Uint8Array(LAW_STATION_CAPACITY),
    stationDisabled: new Uint8Array(LAW_STATION_CAPACITY),
    sx: new Float64Array(LAW_STATION_CAPACITY),
    sy: new Float64Array(LAW_STATION_CAPACITY),
    sz: new Float64Array(LAW_STATION_CAPACITY),
    sr: new Float64Array(LAW_STATION_CAPACITY),
    zones: 0,
    zoneA: new Int8Array(LAW_ZONE_CAPACITY),
    zoneB: new Int8Array(LAW_ZONE_CAPACITY),
    zx: new Float64Array(LAW_ZONE_CAPACITY),
    zy: new Float64Array(LAW_ZONE_CAPACITY),
    zz: new Float64Array(LAW_ZONE_CAPACITY),
  }
}

/** A zone as the manifest (`LocalZone`) and the system view (`ZoneView`) both carry it. */
export interface LawZoneSource {
  sideA: FactionId
  sideB: FactionId
  node: { pos: readonly [number, number, number] | [number, number, number] }
}

/**
 * The law index of a view faction id: −1 for none and for the player corporation. The corporation is never a law
 * faction (M5 spec §27.1–27.2, D-424): it books, scans, fines and refuses nobody; `LawWorld.corpHeld` and
 * `stationCorp` carry where it holds or owns. The worker's `jurisdiction()` agrees (−1 in corporation space).
 */
export function lawFactionOf(id: CorpFactionId | null): number {
  return id === null || id === PLAYER_FACTION_ID ? -1 : FACTION[id]
}

/** A station's gameplay radius R (metres) for the law world (scale SC-5e2). Message rate. */
export type LawStationRadius = (s: StationView) => number

/**
 * The default `LawStationRadius`: R of the view's model (`stationRadiusOf(assetId)`). A home station's view carries the
 * Reach site's asset, not M2's model the runtime shows, so the subsystem passes a layout-aware resolver (D-912).
 */
export function viewStationRadius(s: StationView): number {
  return stationRadiusOf(s.assetId)
}

/** Refills `w` for a system (message rate): its stations (with their radii, `radiusOf`), zones and claim holder. */
export function fillLawWorld(
  w: LawWorld,
  systemId: string | null,
  stations: readonly StationView[],
  zones: readonly LawZoneSource[],
  holder: CorpFactionId | null,
  radiusOf: LawStationRadius = viewStationRadius,
): void {
  w.systemId = systemId
  w.holder = lawFactionOf(holder)
  w.jurisdiction = w.holder >= 0 && FACTION_OUTLAW[w.holder] === 0 ? w.holder : -1
  w.corpHeld = holder === PLAYER_FACTION_ID
  const n = Math.min(stations.length, LAW_STATION_CAPACITY)
  w.stationIds.length = n
  for (let i = 0; i < n; i++) {
    const s = stations[i]
    w.stationIds[i] = s.id
    w.stationOwner[i] = lawFactionOf(s.owner)
    w.stationCorp[i] = s.owner === PLAYER_FACTION_ID ? 1 : 0
    w.stationDisabled[i] = s.disabledBy !== null ? 1 : 0
    w.sx[i] = s.pos[0]
    w.sy[i] = s.pos[1]
    w.sz[i] = s.pos[2]
    w.sr[i] = radiusOf(s)
  }
  w.stations = n
  const m = Math.min(zones.length, LAW_ZONE_CAPACITY)
  for (let i = 0; i < m; i++) {
    const zn = zones[i]
    w.zoneA[i] = FACTION[zn.sideA]
    w.zoneB[i] = FACTION[zn.sideB]
    w.zx[i] = zn.node.pos[0]
    w.zy[i] = zn.node.pos[1]
    w.zz[i] = zn.node.pos[2]
  }
  w.zones = m
}

/** Clears the local world (the system was left). */
export function clearLawWorld(w: LawWorld): void {
  w.systemId = null
  w.jurisdiction = -1
  w.holder = -1
  w.corpHeld = false
  w.stations = 0
  w.stationIds.length = 0
  w.stationCorp.fill(0)
  w.zones = 0
}

/** Receives a detected crime: kind, the victim's faction index, the `reportCrime` value. */
export type CrimeSink = (kind: CrimeKind, victim: number, value: number) => void

const MURDER_RING = 16

export interface CrimeDetector {
  cursor: number
  spaceCursor: number
  readonly tuning: CrimeTuning
  /** Sim time each entity last damaged the player (−Infinity = never). */
  readonly lastHitPlayer: Float64Array
  /** Sim time of each victim's last reported assault (−Infinity = never). */
  readonly lastAssault: Float64Array
  /** Murders: death point, time, victim faction (ring). */
  readonly murderX: Float64Array
  readonly murderY: Float64Array
  readonly murderZ: Float64Array
  readonly murderT: Float64Array
  readonly murderF: Int8Array
  murderHead: number
  /** Wrecks flagged "murdered by the player": position, murder time, faction, reported (ring). */
  readonly wreckX: Float64Array
  readonly wreckY: Float64Array
  readonly wreckZ: Float64Array
  readonly wreckT: Float64Array
  readonly wreckF: Int8Array
  readonly wreckDone: Uint8Array
  wreckHead: number
  /** Hull build value per sim ship def index (murder `value`), for `defsOf`. */
  defValue: Float64Array
  defsOf: Sim | null
}

export function createCrimeDetector(tuning: CrimeTuning = defaultCrimeTuning()): CrimeDetector {
  const d: CrimeDetector = {
    cursor: 0,
    spaceCursor: 0,
    tuning,
    lastHitPlayer: new Float64Array(MAX_ENTITIES),
    lastAssault: new Float64Array(MAX_ENTITIES),
    murderX: new Float64Array(MURDER_RING),
    murderY: new Float64Array(MURDER_RING),
    murderZ: new Float64Array(MURDER_RING),
    murderT: new Float64Array(MURDER_RING),
    murderF: new Int8Array(MURDER_RING),
    murderHead: 0,
    wreckX: new Float64Array(MURDER_RING),
    wreckY: new Float64Array(MURDER_RING),
    wreckZ: new Float64Array(MURDER_RING),
    wreckT: new Float64Array(MURDER_RING),
    wreckF: new Int8Array(MURDER_RING),
    wreckDone: new Uint8Array(MURDER_RING),
    wreckHead: 0,
    defValue: new Float64Array(0),
    defsOf: null,
  }
  resetCrimeDetector(d)
  return d
}

/** Forgets every hit, murder and wreck (a new system or sim). Cursors are left to the caller. */
export function resetCrimeDetector(d: CrimeDetector): void {
  d.lastHitPlayer.fill(-Infinity)
  d.lastAssault.fill(-Infinity)
  d.murderT.fill(-Infinity)
  d.murderF.fill(-1)
  d.wreckT.fill(-Infinity)
  d.wreckF.fill(-1)
  d.wreckDone.fill(1)
  d.murderHead = 0
  d.wreckHead = 0
}

/** Hull build value (Σ buildCost × basePrice) by ship def id (both `shipDef` and `patrolShipDef`). */
const HULL_VALUE_BY_DEF = new Map<string, number>()
{
  const price = new Map<string, number>(waresJson.wares.map((w) => [w.id, w.basePrice]))
  for (const h of hullsJson.hulls) {
    let v = 0
    for (const [ware, qty] of Object.entries(h.buildCost)) v += (qty ?? 0) * (price.get(ware) ?? 0)
    if (!HULL_VALUE_BY_DEF.has(h.shipDef)) HULL_VALUE_BY_DEF.set(h.shipDef, v)
    if (h.patrolShipDef !== null && !HULL_VALUE_BY_DEF.has(h.patrolShipDef)) HULL_VALUE_BY_DEF.set(h.patrolShipDef, v)
  }
}

/** The murder `value` of a ship def id: its hull's build value (0 when no hull flies it). */
export function hullValueOfDef(defId: string): number {
  return HULL_VALUE_BY_DEF.get(defId) ?? 0
}

function indexDefs(d: CrimeDetector, sim: Sim): void {
  if (d.defsOf === sim) return
  d.defsOf = sim
  d.defValue = new Float64Array(sim.defs.length)
  for (let i = 0; i < sim.defs.length; i++) d.defValue[i] = hullValueOfDef(sim.defs[i].id)
}

function dist2(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const dx = ax - bx
  const dy = ay - by
  const dz = az - bz
  return dx * dx + dy * dy + dz * dz
}

/** True when the player (at px,py,pz) is inside an active local zone with faction `f` as a side. */
export function inLocalZone(w: LawWorld, f: number, px: number, py: number, pz: number, rangeM: number): boolean {
  const r2 = rangeM * rangeM
  for (let i = 0; i < w.zones; i++) {
    if ((w.zoneA[i] === f || w.zoneB[i] === f) && dist2(px, py, pz, w.zx[i], w.zy[i], w.zz[i]) <= r2) return true
  }
  return false
}

/**
 * The witness rule: a live ship (not `player`, not `victim`) of the jurisdiction's or the victim's team within `rangeM`
 * of the player, or a station owned by either faction within `rangeM` of its hull (`|p − centre| − R ≤ rangeM`, i.e.
 * the centre distance ≤ R + rangeM; scale SC-5e2). Allocation-free. A station with no law owner (none,
 * or the corporation) never witnesses: with no jurisdiction (−1) it used to match `o === j` (D-424; in corporation
 * space only the victim's ships and stations witness).
 */
export function witnessed(sim: Sim, w: LawWorld, player: number, victim: number, victimFaction: number, rangeM: number): boolean {
  const px = Pos64.x[player]
  const py = Pos64.y[player]
  const pz = Pos64.z[player]
  const r2 = rangeM * rangeM
  const j = w.jurisdiction
  const vTeam = FACTION_TEAM[victimFaction]
  const jTeam = j >= 0 ? FACTION_TEAM[j] : -2
  const ships = sim.ships.dense
  for (let i = 0; i < ships.length; i++) {
    const e = ships[i]
    if (e === player || e === victim || Life.alive[e] !== 1) continue
    const t = Team.id[e]
    if (t !== vTeam && t !== jTeam) continue
    if (dist2(px, py, pz, Pos64.x[e], Pos64.y[e], Pos64.z[e]) <= r2) return true
  }
  for (let i = 0; i < w.stations; i++) {
    const o = w.stationOwner[i]
    if (o < 0 || (o !== victimFaction && o !== j)) continue
    const reach = rangeM + w.sr[i]
    if (dist2(px, py, pz, w.sx[i], w.sy[i], w.sz[i]) <= reach * reach) return true
  }
  return false
}

/** The victim's faction index when attacking it could be a crime, else −1. */
function lawfulVictim(d: CrimeDetector, w: LawWorld, player: number, victim: number, now: number): number {
  if (victim < 0 || victim === player) return -1
  const team = Team.id[victim]
  const f = TEAM_FACTION[team]
  if (f < 0 || FACTION_OUTLAW[f] === 1) return -1
  if (isStandingHostile(PLAYER_TEAM_ID, team)) return -1
  if (now - d.lastHitPlayer[victim] < d.tuning.graceS) return -1
  if (inLocalZone(w, f, Pos64.x[player], Pos64.y[player], Pos64.z[player], d.tuning.zoneRangeM)) return -1
  return f
}

function rememberMurder(d: CrimeDetector, x: number, y: number, z: number, now: number, f: number): void {
  const k = d.murderHead
  d.murderX[k] = x
  d.murderY[k] = y
  d.murderZ[k] = z
  d.murderT[k] = now
  d.murderF[k] = f
  d.murderHead = (k + 1) % MURDER_RING
}

function flagWreck(d: CrimeDetector, x: number, y: number, z: number, now: number): void {
  const r2 = d.tuning.wreckMatchM * d.tuning.wreckMatchM
  for (let i = 0; i < MURDER_RING; i++) {
    if (d.murderF[i] < 0 || now - d.murderT[i] > d.tuning.piracyWindowS) continue
    if (dist2(x, y, z, d.murderX[i], d.murderY[i], d.murderZ[i]) > r2) continue
    const k = d.wreckHead
    d.wreckX[k] = x
    d.wreckY[k] = y
    d.wreckZ[k] = z
    d.wreckT[k] = d.murderT[i]
    d.wreckF[k] = d.murderF[i]
    d.wreckDone[k] = 0
    d.wreckHead = (k + 1) % MURDER_RING
    d.murderF[i] = -1
    return
  }
}

function checkPiracy(d: CrimeDetector, x: number, y: number, z: number, now: number, report: CrimeSink): void {
  const r2 = d.tuning.piracyRangeM * d.tuning.piracyRangeM
  for (let i = 0; i < MURDER_RING; i++) {
    if (d.wreckDone[i] === 1 || now - d.wreckT[i] > d.tuning.piracyWindowS) continue
    if (dist2(x, y, z, d.wreckX[i], d.wreckY[i], d.wreckZ[i]) > r2) continue
    d.wreckDone[i] = 1
    report('piracy', d.wreckF[i], 0)
    return
  }
}

/**
 * Reads the new combat and space events and reports crimes (per tick; allocation-free). Call it at the sim's
 * `deaths` stage (or right after the step) so a kill's victim entity is still readable.
 */
export function stepCrimes(d: CrimeDetector, sim: Sim, w: LawWorld, space: SpaceEventRing | null, report: CrimeSink): void {
  indexDefs(d, sim)
  const now = sim.time
  const player = sim.playerEid
  const ring = sim.events
  const t = d.tuning
  for (let i = firstReadable(ring, d.cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const type = ring.type[s]
    if (type !== EV.hitHull && type !== EV.hitShield && type !== EV.kill) continue
    const victim = ring.eid[s]
    const attacker = ring.other[s]
    if (player < 0) continue
    if (victim === player) {
      if (attacker >= 0 && type !== EV.kill) d.lastHitPlayer[attacker] = now
      continue
    }
    if (attacker !== player || w.systemId === null) continue
    const f = lawfulVictim(d, w, player, victim, now)
    if (f < 0) continue
    if (type === EV.kill) {
      if (!witnessed(sim, w, player, victim, f, t.witnessM)) continue
      rememberMurder(d, ring.x[s], ring.y[s], ring.z[s], now, f)
      const def = Ship.defIndex[victim]
      report('murder', f, def < d.defValue.length ? d.defValue[def] : 0)
      continue
    }
    if (now - d.lastAssault[victim] < t.throttleS) continue
    if (!witnessed(sim, w, player, victim, f, t.witnessM)) continue
    d.lastAssault[victim] = now
    report('assault', f, victim)
  }
  d.cursor = ring.head
  if (space === null) return
  for (let i = firstReadableSpace(space, d.spaceCursor); i < space.head; i++) {
    const s = spaceEventSlot(i)
    const type = space.type[s]
    if (type === SEV.wreckSpawned) flagWreck(d, space.x[s], space.y[s], space.z[s], now)
    else if (type === SEV.partSecured) checkPiracy(d, space.x[s], space.y[s], space.z[s], now, report)
  }
  d.spaceCursor = space.head
}

/**
 * Who opened each entity's current exchange of fire with the player (M7 S1, D-339; spec §5 "the guardian fired
 * first"). A hit either way within `graceS` of the last one continues the exchange; a longer gap starts a new one,
 * opened by whoever hits first. Allocation-free after creation.
 */
export interface EngagementLog {
  /** Sim time of the last hit between the entity and the player, either way (−Infinity = never). */
  readonly lastContact: Float64Array
  /** 1 when the entity opened the current exchange (it hit the player first), else 0. */
  readonly theyOpened: Uint8Array
  graceS: number
}

export function createEngagementLog(graceS: number = lawJson.crimes.assault.graceS): EngagementLog {
  const log: EngagementLog = { lastContact: new Float64Array(MAX_ENTITIES), theyOpened: new Uint8Array(MAX_ENTITIES), graceS }
  log.lastContact.fill(-Infinity)
  return log
}

/**
 * Records one combat event at sim time `now` (any type; only player hits and hits on the player count). A kill of
 * the player ends nothing: the attacker keeps its record until its exchange lapses.
 */
export function noteEngagement(log: EngagementLog, type: number, victim: number, attacker: number, player: number, now: number): void {
  if (player < 0 || attacker < 0 || victim < 0) return
  if (type !== EV.hitHull && type !== EV.hitShield && type !== EV.kill) return
  let other: number
  let opened: number
  if (victim === player) {
    other = attacker
    opened = 1
  } else if (attacker === player) {
    other = victim
    opened = 0
  } else {
    return
  }
  if (other >= MAX_ENTITIES) return
  if (!(now - log.lastContact[other] < log.graceS)) log.theyOpened[other] = opened
  log.lastContact[other] = now
}

/** True when entity `e` opened its current exchange with the player (it fired first) at sim time `now`. */
export function firedFirst(log: EngagementLog, e: number, now: number): boolean {
  if (e < 0 || e >= MAX_ENTITIES) return false
  return log.theyOpened[e] === 1 && now - log.lastContact[e] < log.graceS
}

/** Forgets entity `e` (it died; its id may be reused). */
export function forgetEngagement(log: EngagementLog, e: number): void {
  if (e < 0 || e >= MAX_ENTITIES) return
  log.lastContact[e] = -Infinity
  log.theyOpened[e] = 0
}
